// activity.ts — is this person actually here?
//
// AN OPEN TAB IS NOT A PERSON. Andrew's laptop sat locked on his desk for five
// hours with the console open and a thread selected. It kept reporting "I am
// reading this" every fifteen seconds the whole time, so the server believed
// that thread was read — and his phone's unread marker was cleared, over and
// over, for a message he had never seen.
//
// Slack's model, which is the one worth copying: you are ACTIVE when the app
// is open AND you have done something recently. Otherwise you are AWAY. A
// locked machine, a backgrounded app and a person who wandered off are all the
// same answer, and it is not "here".
//
// The browser cannot see the OS lock screen directly, so this uses the two
// signals it does have:
//
//   * page visibility — locking a Mac or switching apps hides the page
//   * recent input — pointer, key, touch or scroll
//
// Deliberately NOT `window.onfocus` alone: a focused window belonging to
// somebody who left is still an empty chair.
const IDLE_AFTER_MS = 10 * 60 * 1000

/** Cheap enough to attach to noisy events; nothing here does work per event. */
const INPUT_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'] as const

export interface ActivityTracker {
  /** Whether this person counts as present right now. */
  isActive: () => boolean
  /**
   * Whether they are actually looking at this window.
   *
   * PRESENT AND LOOKING ARE DIFFERENT. A console open behind another app is
   * still `visibilityState: 'visible'` — the browser only calls it hidden when
   * minimised or in a background tab. So somebody with the window buried
   * counted as reading whatever thread was selected, and we skipped paging
   * them about it. Andrew: it alerts when minimised, "but not if it's up and
   * just not visible on the screen."
   *
   * Focus is the signal that separates the two, and it is the one Slack uses:
   * an unfocused window is somewhere you are not.
   */
  isFocused: () => boolean
  /** Called whenever the answer CHANGES, never on every heartbeat. */
  subscribe: (listener: (active: boolean) => void) => () => void
  stop: () => void
}

export interface ActivityDeps {
  idleAfterMs?: number
  now?: () => number
  /** Test seam; defaults to the real timer. */
  setIntervalImpl?: (fn: () => void, ms: number) => ReturnType<typeof setInterval>
  clearIntervalImpl?: (handle: ReturnType<typeof setInterval>) => void
}

/**
 * How often to re-check whether the idle threshold has passed.
 *
 * A poll rather than a timer per interaction: input events are frequent and
 * resetting a timeout on each one is more work for a worse answer.
 */
const CHECK_INTERVAL_MS = 30_000

export function createActivityTracker(deps: ActivityDeps = {}): ActivityTracker {
  const idleAfter = deps.idleAfterMs ?? IDLE_AFTER_MS
  const now = deps.now ?? Date.now
  const setIntervalFn = deps.setIntervalImpl ?? ((fn, ms) => setInterval(fn, ms))
  const clearIntervalFn = deps.clearIntervalImpl ?? ((h) => clearInterval(h))

  let lastInput = now()
  let active = true
  let focused = typeof document === 'undefined' || typeof document.hasFocus !== 'function' || document.hasFocus()
  const listeners = new Set<(active: boolean) => void>()

  function visible(): boolean {
    return typeof document === 'undefined' || document.visibilityState === 'visible'
  }

  function compute(): boolean {
    return visible() && now() - lastInput < idleAfter
  }

  function settle(): void {
    const next = compute()
    if (next === active) return
    active = next
    for (const listener of listeners) listener(active)
  }

  function onInput(): void {
    lastInput = now()
    // Coming back is the transition people notice, so it is applied at once
    // rather than waiting up to half a minute for the next poll.
    if (!active) settle()
  }

  function onFocus(): void {
    focused = true
    lastInput = now()
    settle()
  }

  function onBlur(): void {
    focused = false
    // NOT a change in `active`: somebody who alt-tabbed to look something up
    // is still at their desk and still available for a hand-off. Only what
    // they are READING has changed.
  }

  function onVisibility(): void {
    // Hiding is immediate and certain — no need to wait out the idle timer for
    // a screen that is definitely locked.
    if (visible()) lastInput = now()
    settle()
  }

  const hasDom = typeof document !== 'undefined' && typeof window !== 'undefined'
  if (hasDom) {
    for (const event of INPUT_EVENTS) {
      window.addEventListener(event, onInput, { passive: true })
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    window.addEventListener('blur', onBlur)
  }

  const poll = setIntervalFn(settle, CHECK_INTERVAL_MS)

  return {
    isActive: () => active,
    isFocused: () => focused && visible(),
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    stop: () => {
      clearIntervalFn(poll)
      listeners.clear()
      if (hasDom) {
        for (const event of INPUT_EVENTS) window.removeEventListener(event, onInput)
        document.removeEventListener('visibilitychange', onVisibility)
        window.removeEventListener('focus', onFocus)
        window.removeEventListener('blur', onBlur)
      }
    },
  }
}

/** The one every real caller shares; a test builds its own. */
export const activity = createActivityTracker()
